/**
 * Configuracao declarativa do PROGRESSO das boas praticas na lista nominal do /saude.
 *
 * Descreve, por eixo, como calcular o progresso de cada boa pratica a partir da linha do paciente
 * (campos retornados pela lista nominal). E agnostico a fonte: PEC e DWH expoem os MESMOS nomes de
 * campo (total_consultas, total_antrop, dt_primeira_visita, dt_segunda_visita, doses_*, etc.).
 *
 * Opt-in por eixo (mesmo padrao de PAINEL_PONTUACAO_BOA_PRATICA / BOA_PRATICA_CLICAVEL):
 * so os eixos presentes neste mapa exibem a fracao inline + o icone de detalhe.
 */

/** Tipos de progresso de uma boa pratica. */
export type ProgressoDescritor =
  // Feito ou nao (ex.: 1a consulta ate 30 dias). Progresso 0/1.
  | { key: string; letra: string; label: string; tipo: 'existencia'; dateKey?: string }
  // Contagem simples com meta (ex.: 9+ consultas, 9+ registros de peso e altura).
  | { key: string; letra: string; label: string; tipo: 'contagem'; countKey: string; meta: number; dateKey?: string }
  // Conjunto de janelas/etapas distintas (ex.: 2 visitas em periodos diferentes).
  | { key: string; letra: string; label: string; tipo: 'janelas'; meta: number; janelas: { label: string; dateKey: string }[] }
  // Conjunto de grupos, cada um com sua propria meta (ex.: esquema vacinal com 4 grupos).
  | { key: string; letra: string; label: string; tipo: 'grupos'; meta: number; grupos: { label: string; countKey: string; meta: number }[] };

export type EixoProgresso = ProgressoDescritor[];

/** Numero (seguro) a partir de um campo possivelmente string/null da linha. */
function num(v: any): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Calcula o progresso (atual/meta) e se esta concluido para uma boa pratica de um paciente.
 * `concluido` usa o booleano oficial retornado pelo backend (ind_x), que ja considera as regras
 * exatas da nota; `atual`/`meta` servem so para a visualizacao da fracao/barra.
 */
export function calcProgresso(
  paciente: Record<string, any>,
  desc: ProgressoDescritor,
): { atual: number; meta: number; concluido: boolean } {
  const concluido = num(paciente[desc.key]) === 1;
  switch (desc.tipo) {
    case 'existencia':
      return { atual: concluido ? 1 : 0, meta: 1, concluido };
    case 'contagem': {
      const atual = Math.min(num(paciente[desc.countKey]), desc.meta);
      return { atual, meta: desc.meta, concluido };
    }
    case 'janelas': {
      const atual = desc.janelas.filter((j) => !!paciente[j.dateKey]).length;
      return { atual, meta: desc.meta, concluido };
    }
    case 'grupos': {
      const atual = desc.grupos.filter((g) => num(paciente[g.countKey]) >= g.meta).length;
      return { atual, meta: desc.meta, concluido };
    }
    default:
      return { atual: 0, meta: 1, concluido };
  }
}

export const BOA_PRATICA_PROGRESSO: Record<string, EixoProgresso> = {
  'c2-criancas': [
    { key: 'ind_a', letra: 'A', label: '1a consulta ate 30 dias de vida', tipo: 'existencia', dateKey: 'dt_primeira_consulta' },
    { key: 'ind_b', letra: 'B', label: '9+ consultas (ate 2 anos)', tipo: 'contagem', countKey: 'total_consultas', meta: 9, dateKey: 'dt_ultima_consulta' },
    { key: 'ind_c', letra: 'C', label: '9+ registros de peso e altura', tipo: 'contagem', countKey: 'total_antrop', meta: 9, dateKey: 'dt_ultima_antropometria' },
    {
      key: 'ind_d', letra: 'D', label: '2+ visitas do ACS/TACS', tipo: 'janelas', meta: 2,
      janelas: [
        { label: '1a visita (ate 30 dias)', dateKey: 'dt_primeira_visita' },
        { label: '2a visita (ate 6 meses)', dateKey: 'dt_segunda_visita' },
      ],
    },
    {
      key: 'ind_e', letra: 'E', label: 'Vacinas em dia', tipo: 'grupos', meta: 4,
      grupos: [
        { label: 'Pentavalente', countKey: 'doses_penta', meta: 3 },
        { label: 'VIP / Polio', countKey: 'doses_polio', meta: 3 },
        { label: 'Triplice viral (SCR)', countKey: 'doses_scr', meta: 2 },
        { label: 'Pneumococica 10', countKey: 'doses_pneumo', meta: 2 },
      ],
    },
  ],
  'c3-gestantes': [
    { key: 'ind_a', letra: 'A', label: 'Inicio precoce do pre-natal (ate 12 semanas)', tipo: 'existencia', dateKey: 'dt_primeira_consulta' },
    { key: 'ind_b', letra: 'B', label: '7+ consultas de pre-natal', tipo: 'contagem', countKey: 'consultas_prenatal', meta: 7, dateKey: 'dt_ultima_consulta' },
    { key: 'ind_c', letra: 'C', label: '7+ afericoes de pressao arterial', tipo: 'contagem', countKey: 'afericoes_pa', meta: 7, dateKey: 'dt_ultima_pa' },
    { key: 'ind_d', letra: 'D', label: '7+ registros antropometricos', tipo: 'contagem', countKey: 'registros_antrop', meta: 7, dateKey: 'dt_ultima_antrop' },
    { key: 'ind_e', letra: 'E', label: '3+ visitas do ACS na gestacao', tipo: 'contagem', countKey: 'visitas_gestacao', meta: 3, dateKey: 'dt_ultima_visita' },
    { key: 'ind_f', letra: 'F', label: 'Vacina dTpa na gestacao', tipo: 'existencia', dateKey: 'dt_dtpa' },
    { key: 'ind_g', letra: 'G', label: 'Exames do 1o trimestre (sifilis, HIV, hepB, hepC)', tipo: 'existencia', dateKey: 'dt_exame_1t' },
    { key: 'ind_h', letra: 'H', label: 'Exames do 3o trimestre (sifilis, HIV)', tipo: 'existencia', dateKey: 'dt_exame_3t' },
    { key: 'ind_i', letra: 'I', label: 'Consulta puerperal (medico/enfermeiro)', tipo: 'existencia', dateKey: 'dt_consulta_puerperio' },
    { key: 'ind_j', letra: 'J', label: 'Visita puerperal do ACS', tipo: 'existencia', dateKey: 'dt_visita_puerperio' },
    { key: 'ind_k', letra: 'K', label: 'Saude bucal na gestacao', tipo: 'existencia', dateKey: 'dt_saude_bucal' },
  ],
  'c4-diabeticos': [
    { key: 'ind_a', letra: 'A', label: 'Consulta medico/enfermeiro (6m)', tipo: 'existencia', dateKey: 'dt_ultima_consulta' },
    { key: 'ind_b', letra: 'B', label: 'Afericao de pressao arterial (6m)', tipo: 'existencia', dateKey: 'dt_ultima_pa' },
    { key: 'ind_c', letra: 'C', label: 'Peso e altura (12m)', tipo: 'existencia', dateKey: 'dt_ultima_antropometria' },
    {
      key: 'ind_d', letra: 'D', label: '2+ visitas do ACS/TACS (intervalo >= 30 dias)', tipo: 'janelas', meta: 2,
      janelas: [
        { label: '1a visita do ACS/TACS', dateKey: 'dt_primeira_visita' },
        { label: '2a visita (>= 30 dias apos a 1a)', dateKey: 'dt_segunda_visita' },
      ],
    },
    { key: 'ind_e', letra: 'E', label: 'Hemoglobina glicada (12m)', tipo: 'existencia', dateKey: 'dt_ultima_hba1c' },
    { key: 'ind_f', letra: 'F', label: 'Avaliacao do pe diabetico (12m)', tipo: 'existencia', dateKey: 'dt_ultima_pe_diabetico' },
  ],
  'c5-hipertensos': [
    { key: 'ind_a', letra: 'A', label: 'Consulta medico/enfermeiro (6m)', tipo: 'existencia', dateKey: 'dt_ultima_consulta' },
    { key: 'ind_b', letra: 'B', label: 'Afericao de pressao arterial (6m)', tipo: 'existencia', dateKey: 'dt_ultima_pa' },
    { key: 'ind_c', letra: 'C', label: 'Peso e altura (12m)', tipo: 'existencia', dateKey: 'dt_ultima_antropometria' },
    {
      key: 'ind_d', letra: 'D', label: '2+ visitas do ACS/TACS (intervalo >= 30 dias)', tipo: 'janelas', meta: 2,
      janelas: [
        { label: '1a visita do ACS/TACS', dateKey: 'dt_primeira_visita' },
        { label: '2a visita (>= 30 dias apos a 1a)', dateKey: 'dt_segunda_visita' },
      ],
    },
  ],
  'c6-idosos': [
    { key: 'ind_a', letra: 'A', label: 'Consulta medico/enfermeiro (12m)', tipo: 'existencia', dateKey: 'dt_ultima_consulta' },
    { key: 'ind_b', letra: 'B', label: 'Peso e altura (12m)', tipo: 'existencia', dateKey: 'dt_ultima_antropometria' },
    {
      key: 'ind_c', letra: 'C', label: '2+ visitas do ACS/TACS (intervalo >= 30 dias)', tipo: 'janelas', meta: 2,
      janelas: [
        { label: '1a visita do ACS/TACS', dateKey: 'dt_primeira_visita' },
        { label: '2a visita (>= 30 dias apos a 1a)', dateKey: 'dt_segunda_visita' },
      ],
    },
    { key: 'ind_d', letra: 'D', label: 'Vacina influenza (12m)', tipo: 'existencia', dateKey: 'dt_ultima_vacina' },
  ],
  'c7-mulheres': [
    { key: 'ind_a', letra: 'A', label: 'Citopatologico (colo do utero, 36m)', tipo: 'existencia', dateKey: 'dt_ultima_citopat' },
    { key: 'ind_b', letra: 'B', label: 'Vacina HPV (1+ dose)', tipo: 'existencia', dateKey: 'dt_ultima_hpv' },
    { key: 'ind_c', letra: 'C', label: 'Saude sexual e reprodutiva (12m)', tipo: 'existencia', dateKey: 'dt_ultima_saude_sexual' },
    { key: 'ind_d', letra: 'D', label: 'Mamografia (cancer de mama, 24m)', tipo: 'existencia', dateKey: 'dt_ultima_mamografia' },
  ],
};
